import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader2, Shield, User } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const FieldStaffLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginType, setLoginType] = useState('admin'); // 'admin' or 'staff'

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const formData = new URLSearchParams();
      formData.append('username', email);
      formData.append('password', password);

      const response = await axios.post(
        `${API_URL}/api/field-staff/auth/login`,
        formData,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );

      const { access_token, user } = response.data;

      // Store token and user info
      localStorage.setItem('field_staff_token', access_token);
      localStorage.setItem('field_staff_user', JSON.stringify(user));

      // Redirect based on role
      if (user.role === 'super_admin' || user.role === 'admin') {
        navigate('/monitoring-dashboard/field-staff/admin');
      } else if (user.role === 'staff') {
        navigate('/monitoring-dashboard/field-staff/staff');
      } else {
        setError('Invalid user role');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 flex items-center justify-center p-4">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-32 h-32 md:w-64 md:h-64 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 md:w-96 md:h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 md:w-[500px] md:h-[500px] bg-blue-400/10 rounded-full blur-3xl"></div>
      </div>
      
      <Card className="w-full max-w-md shadow-2xl relative z-10 backdrop-blur-sm bg-white/95 border-0 rounded-2xl">
        <CardHeader className="space-y-1 text-center pb-4 md:pb-6">
          {/* Netix Fibernet Logo - Main branding */}
          <div className="flex justify-center mb-4 md:mb-6">
            <img 
              src="/netix-logo.png" 
              alt="Netix Fibernet" 
              className="h-24 w-24 md:h-32 md:w-32 object-contain drop-shadow-xl"
            />
          </div>
          <CardTitle className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
            Field Staff Management
          </CardTitle>
          <CardDescription className="text-sm md:text-base text-gray-600">
            Real-time tracking & task management system
          </CardDescription>
        </CardHeader>

        <CardContent className="px-4 md:px-6">
          {/* Login Type Selector - Mobile optimized */}
          <div className="flex gap-2 mb-4 md:mb-6">
            <Button
              type="button"
              variant={loginType === 'admin' ? 'default' : 'outline'}
              className={`flex-1 h-11 md:h-12 text-sm md:text-base transition-all duration-300 ${
                loginType === 'admin' 
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/30' 
                  : 'hover:bg-blue-50'
              }`}
              onClick={() => setLoginType('admin')}
            >
              <Shield className="h-4 w-4 mr-2" />
              Admin
            </Button>
            <Button
              type="button"
              variant={loginType === 'staff' ? 'default' : 'outline'}
              className={`flex-1 h-11 md:h-12 text-sm md:text-base transition-all duration-300 ${
                loginType === 'staff' 
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 shadow-lg shadow-green-500/30' 
                  : 'hover:bg-green-50'
              }`}
              onClick={() => setLoginType('staff')}
            >
              <User className="h-4 w-4 mr-2" />
              Staff
            </Button>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm md:text-base font-medium">{loginType === 'admin' ? 'Admin' : 'Staff'} Email</Label>
              <Input
                id="email"
                type="email"
                placeholder={loginType === 'admin' ? 'admin@company.com' : 'staff@company.com'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 md:h-14 text-base focus:ring-2 focus:ring-blue-500 transition-all rounded-xl border-gray-200"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm md:text-base font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 md:h-14 text-base focus:ring-2 focus:ring-blue-500 transition-all rounded-xl border-gray-200"
              />
            </div>

            {error && (
              <Alert variant="destructive" className="py-3 rounded-xl">
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className={`w-full h-12 md:h-14 text-base md:text-lg font-semibold transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] rounded-xl ${
                loginType === 'admin'
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/30'
                  : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg shadow-green-500/30'
              }`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            <p>Secure access to field operations dashboard</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FieldStaffLogin;
